import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';

import {
  getSpecificInstruction,
  deleteSpecificInstruction,
  getSpecificInstructionDetail,
} from '../../../services/specificInstructionService';

import Loader from '../../../components/Loader/Loader';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import Pagination from '../../../components/Pagination/Pagination';

type Instruction = {
  id: number;
  reference_number: string;
  created_at: string;
  title: string;
  site: string;
  status: string | null;

  // API may return either:
  // created_by: 10
  // OR
  // created_by: { id: 10, ... }
  created_by?: number | string | { id?: number | string } | null;
};

type Props = {
  onEdit: (data: any) => void;
  refreshTrigger?: number;
  onDeleteSuccess?: () => void;
  onError?: (message: string) => void;
  isSystemEngineer: boolean;
  searchQuery?: string;
};

const SpecificInstructionTable: React.FC<Props> = ({
  onEdit,
  refreshTrigger,
  onDeleteSuccess,
  onError,
  isSystemEngineer,
  searchQuery = '',
}) => {
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Current logged-in user
  const [currentUserId, setCurrentUserId] = useState<string>('');

  const navigate = useNavigate();

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ============================================================
  // GET CURRENT USER ID
  // ============================================================

  useEffect(() => {
    const userId = localStorage.getItem('currentUserID') || '';

    setCurrentUserId(String(userId));
  }, []);

  // ============================================================
  // FETCH DATA
  // ============================================================

  const fetchData = async (page = 1, search = '') => {
    setLoading(true);

    try {
      const res = await getSpecificInstruction(page, search);

      const rawItems = res?.data || res || [];

      /*
       * Normalize API response.
       *
       * Some APIs return:
       *
       * created_by: 10
       *
       * while others return:
       *
       * created_by: {
       *   id: 10,
       *   name: "John"
       * }
       *
       * We convert both into:
       *
       * created_by: 10
       */

      const items: Instruction[] = Array.isArray(rawItems)
        ? rawItems.map((item: any) => ({
            ...item,

            created_by: typeof item.created_by === 'object' ? item.created_by?.id : item.created_by,
          }))
        : [];

      setInstructions(items);

      const meta = res?.meta || res?.data?.meta || null;

      if (meta?.current_page !== currentPage) {
        setCurrentPage(meta?.current_page || page);
      }

      setTotalPages(meta?.last_page || 1);
      setPerPage(meta?.per_page || 10);
    } catch (error) {
      console.error('Failed to fetch Specific Instructions:', error);

      setInstructions([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // ============================================================
  // FETCH ON:
  // INITIAL LOAD
  // PAGE CHANGE
  // REFRESH
  // SEARCH
  // ============================================================

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchData(currentPage, searchQuery);
    }, 150);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [currentPage, refreshTrigger, searchQuery]);

  // ============================================================
  // EDIT / DELETE PERMISSION
  // ============================================================

  /*
   * BUSINESS RULE:
   *
   * 1. User must be System Engineer.
   *
   * 2. User must be the creator of the application.
   *
   * 3. Application status must currently be "New".
   *
   * Examples:
   *
   * New
   * + Creator
   * + System Engineer
   *       => EDIT / DELETE
   *
   * Accepted
   * + Creator
   * + System Engineer
   *       => NO EDIT / DELETE
   *
   * Authorization Pending
   *       => NO EDIT / DELETE
   *
   * Authorized
   *       => NO EDIT / DELETE
   *
   * Instruction Pending
   *       => NO EDIT / DELETE
   *
   * Rejected -> backend changes status to New
   *       => EDIT / DELETE available again
   */

  const canEditOrDelete = (item: Instruction): boolean => {
    // User must be System Engineer
    if (!isSystemEngineer) {
      return false;
    }

    // Check creator
    const itemCreatedBy = String(item.created_by ?? '');

    const loggedInUser = String(currentUserId ?? '');

    const isCreator = itemCreatedBy !== '' && loggedInUser !== '' && itemCreatedBy === loggedInUser;

    if (!isCreator) {
      return false;
    }

    // Only NEW status allows Edit/Delete
    const status = String(item.status ?? '')
      .trim()
      .toLowerCase();

    return status === 'new';
  };

  // ============================================================
  // DELETE BUTTON
  // ============================================================

  const handleDeleteClick = (item: Instruction, e: React.MouseEvent) => {
    e.stopPropagation();

    // Extra protection
    if (!canEditOrDelete(item)) {
      return;
    }

    setDeleteId(item.id);
    setIsDeleteOpen(true);
  };

  // ============================================================
  // DELETE CONFIRM
  // ============================================================

  const handleDeleteConfirm = async () => {
    if (!deleteId || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);

      await deleteSpecificInstruction(deleteId);

      setIsDeleteOpen(false);
      setDeleteId(null);

      // Refresh current page
      await fetchData(currentPage, searchQuery);

      onDeleteSuccess?.();
    } catch (err: any) {
      console.error('Failed to delete Specific Instruction:', err);

      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete Specific Instruction';

      onError?.(errorMessage);
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
      setDeleteId(null);
    }
  };

  // ============================================================
  // CANCEL DELETE
  // ============================================================

  const handleDeleteCancel = () => {
    if (isDeleting) {
      return;
    }

    setIsDeleteOpen(false);
    setDeleteId(null);
  };

  // ============================================================
  // EDIT
  // ============================================================

  const handleEditClick = async (item: Instruction, e: React.MouseEvent) => {
    e.stopPropagation();

    // Extra frontend protection
    if (!canEditOrDelete(item)) {
      return;
    }

    try {
      const fullData = await getSpecificInstructionDetail(item.id);

      onEdit(fullData.data);
    } catch (err: any) {
      console.error('Error fetching Specific Instruction detail:', err);

      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load data for editing';

      onError?.(errorMessage);
    }
  };

  // ============================================================
  // ROW CLICK
  // ============================================================

  const handleRowClick = (id: number) => {
    navigate(`/SpecificInstruction/${id}`);
  };

  // ============================================================
  // INITIAL LOADING
  // ============================================================

  if (loading && initialLoad) {
    return <Loader />;
  }

  // ============================================================
  // TABLE
  // ============================================================

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border">
          {/* ==================================================
              TABLE HEADER
          ================================================== */}

          <thead>
            <tr className="h-[45px]">
              <th className="border px-4 py-1 text-xs font-medium text-gray-500">Sl. No</th>

              <th className="border px-4 py-1 text-xs font-medium text-gray-500">Ref No.</th>

              <th className="border px-4 py-1 text-xs font-medium text-gray-500">Created Date</th>

              <th className="border px-4 py-1 text-xs font-medium text-gray-500">Title</th>

              <th className="border px-4 py-1 text-xs font-medium text-gray-500">Site</th>

              <th className="border px-4 py-1 text-xs font-medium text-gray-500">Status</th>

              {isSystemEngineer && <th className="border px-4 py-2 text-xs font-medium text-gray-500">Action</th>}
            </tr>
          </thead>

          {/* ==================================================
              TABLE BODY
          ================================================== */}

          <tbody>
            {instructions.length === 0 ? (
              <tr>
                <td colSpan={isSystemEngineer ? 7 : 6} className="border px-4 py-8 text-center text-gray-500">
                  No Specific Instructions found.
                </td>
              </tr>
            ) : (
              instructions.map((item, index) => {
                /*
                 * This determines whether Edit/Delete
                 * should be visible.
                 */
                const showEditDelete = canEditOrDelete(item);

                return (
                  <tr key={item.id} onClick={() => handleRowClick(item.id)} className="hover:bg-gray-50 cursor-pointer">
                    {/* SL NO */}
                    <td className="border px-4 py-2 text-gray-500 text-center">{(currentPage - 1) * perPage + index + 1}</td>

                    {/* REFERENCE NUMBER */}
                    <td className="border px-4 py-2 font-medium">{item.reference_number}</td>

                    {/* CREATED DATE */}
                    <td className="border px-4 py-2 font-medium">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB') : '-'}
                    </td>

                    {/* TITLE */}
                    <td className="border px-4 py-2 font-medium">
                      {item.title?.length > 30 ? `${item.title.slice(0, 30)}...` : item.title}
                    </td>

                    {/* SITE */}
                    <td className="border px-4 py-2 font-medium">{item.site}</td>

                    {/* STATUS */}
                    <td className="border px-4 py-2 font-medium text-purple-600">{item.status || 'New'}</td>

                    {/* ==================================================
                          ACTION COLUMN
                      ================================================== */}

                    {isSystemEngineer && (
                      <td className="border px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        {showEditDelete && (
                          <div className="flex items-center gap-3">
                            {/* EDIT */}
                            <button
                              type="button"
                              onClick={(e) => handleEditClick(item, e)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>

                            {/* DELETE */}
                            <button
                              type="button"
                              onClick={(e) => handleDeleteClick(item, e)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ======================================================
          PAGINATION
      ====================================================== */}

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* ======================================================
          DELETE CONFIRMATION MODAL
      ====================================================== */}

      <ConfirmModal
        isOpen={isDeleteOpen}
        title="Are you sure you want to delete this Specific Instruction?"
        message=""
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </>
  );
};

export default SpecificInstructionTable;
